import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { toast } from 'sonner';
import { getFileIcon } from '../../../../../utils/fileIcons';
import { useGetAttachmentDownloadUrlMutation } from '../../maintenance.mutations';

interface Attachment {
  name: string;
  url?: string;
  key?: string;
  type?: string;
}

interface AttachmentListProps {
  attachments: Attachment[];
  title?: string;
  compact?: boolean;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  title = 'Attachments',
  compact = false,
}) => {
  // Set, not single string, so concurrent downloads of different attachments
  // don't clear each other's spinner state. TanStack's isPending is per-mutation-
  // instance, not per-item, so we keep this local Set for per-item spinners.
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

  const getDownloadUrlMutation = useGetAttachmentDownloadUrlMutation();

  if (!attachments || attachments.length === 0) {
    return null;
  }

  // Normalize attachment data - handle different possible structures
  const normalizedAttachments: Attachment[] = attachments.map((att) => {
    // att is already an Attachment, but may have additional properties from different sources
    const rawAtt = att as Attachment & {
      filename?: string;
      download_url?: string;
      s3_key?: string;
      mime_type?: string;
      content_type?: string;
    };
    return {
      name: rawAtt.name || rawAtt.filename || 'Unknown File',
      url: rawAtt.url || rawAtt.download_url,
      key: rawAtt.key || rawAtt.s3_key,
      type: rawAtt.type || rawAtt.mime_type || rawAtt.content_type,
    };
  });

  // Per-render stable ID — the array index guarantees uniqueness within the
  // list even if two attachments share the same name and lack a key.
  const idFor = (index: number, attachment: Attachment): string =>
    attachment.key ?? `index-${index}`;

  const handleDownload = async (attachment: Attachment, id: string) => {
    // Presigned URLs stored on legacy rows expire after 1 hour, so always
    // fetch a fresh URL via the S3 key when available; fall back to the
    // stored URL only for old rows that lack a key.
    let href = attachment.url;

    if (attachment.key) {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      try {
        const result = await getDownloadUrlMutation.mutateAsync(attachment.key);
        href = result.download_url;
      } catch (err) {
        console.error('Failed to refresh attachment URL', err);
        if (!attachment.url) {
          toast.error(`Failed to download ${attachment.name}`);
          return;
        }
        // legacy row with stored URL — fall through and try it as a best-effort
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }

    if (!href) {
      toast.error(`Cannot download ${attachment.name}: no S3 key`);
      return;
    }

    const link = document.createElement('a');
    link.href = href;
    link.download = attachment.name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (compact) {
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {normalizedAttachments.map((attachment, index) => {
            const id = idFor(index, attachment);
            const isDownloading = downloadingIds.has(id);
            return (
              <Chip
                key={index}
                icon={
                  isDownloading ? (
                    <CircularProgress size={12} />
                  ) : (
                    getFileIcon(attachment.name, attachment.type)
                  )
                }
                label={attachment.name}
                size="small"
                clickable
                disabled={isDownloading}
                onClick={() => handleDownload(attachment, id)}
                sx={{ fontSize: '0.75rem' }}
              />
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title} ({normalizedAttachments.length})
      </Typography>
      <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, py: 0 }}>
        {normalizedAttachments.map((attachment, index) => {
          const id = idFor(index, attachment);
          const isDownloading = downloadingIds.has(id);
          return (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => handleDownload(attachment, id)}
                disabled={isDownloading}
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {getFileIcon(attachment.name, attachment.type)}
                </ListItemIcon>
                <ListItemText
                  primary={attachment.name}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500 },
                  }}
                />
                {isDownloading ? (
                  <CircularProgress size={16} />
                ) : (
                  <DownloadIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};
